"use client";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface OrgSitePolicyRendererProps {
  content: string;
}

export function OrgSitePolicyRenderer({ content }: OrgSitePolicyRendererProps) {
  return (
    <article className="prose prose-gray max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h2 className="mb-6 text-3xl font-bold tracking-tight text-foreground md:text-4xl">
              {children}
            </h2>
          ),
          h2: ({ children }) => (
            <h2 className="mb-4 mt-10 text-xl font-semibold text-foreground border-b border-border pb-3">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="mb-3 mt-6 text-lg font-semibold text-foreground">{children}</h3>
          ),
          p: ({ children }) => (
            <p className="mb-4 leading-relaxed text-muted-foreground">{children}</p>
          ),
          ul: ({ children }) => (
            <ul className="mb-4 list-disc space-y-2 pl-6 text-muted-foreground">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="mb-4 list-decimal space-y-2 pl-6 text-muted-foreground">{children}</ol>
          ),
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
          a: ({ href, children }) => {
            const safeHref =
              href && /^https?:\/\//i.test(href)
                ? href
                : href?.startsWith("mailto:")
                  ? href
                  : undefined;
            return (
              <a
                href={safeHref}
                rel="noopener noreferrer"
                className="font-medium text-primary underline decoration-primary/30 underline-offset-2 transition-colors hover:text-primary/80 hover:decoration-primary"
              >
                {children}
              </a>
            );
          },
          strong: ({ children }) => (
            <strong className="font-semibold text-foreground">{children}</strong>
          ),
          em: ({ children }) => <em className="text-muted-foreground">{children}</em>,
          hr: () => <hr className="my-8 border-border" />,
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-primary/30 pl-4 italic text-muted-foreground">
              {children}
            </blockquote>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </article>
  );
}
